import { Alert, Button, Dialog, DialogContent, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { renameMultiplexPanel } from 'api/multiplexPanels';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { join } from 'lodash';
import React, { useState } from 'react';

const errorMessage = (error: unknown) => {
  const detail = (error as { response?: { body?: { detail?: string | string[] } } })?.response?.body?.detail;
  return Array.isArray(detail)
    ? join(detail, ' ')
    : typeof detail === 'string'
    ? detail
    : 'The panel name could not be saved. Your edit is still here; try again.';
};

export const RenamePanelDialog = ({
  studyId,
  panelId,
  currentName,
  onClose,
}: {
  studyId: string;
  panelId: string;
  currentName: string;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const confirm = useConfirmation();
  const [name, setName] = useState(currentName);
  const trimmedName = name.trim();
  const dirty = name !== currentName;
  const rename = useMutation({
    mutationKey: ['multiplex_panel_mutations', studyId],
    mutationFn: () => renameMultiplexPanel(studyId, { panelId, name: trimmedName }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['multiplex_panels'] }),
        queryClient.invalidateQueries({ queryKey: ['study', studyId] }),
      ]);
      onClose();
    },
  });
  const close = async () => {
    if (rename.isLoading) return;
    if (
      !dirty ||
      (await confirm({
        title: 'Discard changes?',
        text: 'Your unsaved panel name will be discarded.',
        confirmButtonProps: { title: 'Discard changes', color: 'warning', variant: 'contained' },
        cancelButtonProps: { title: 'Keep editing' },
      }))
    )
      onClose();
  };

  return (
    <Dialog open maxWidth="xs" fullWidth onClose={close} aria-labelledby="rename-multiplex-panel-title">
      <DialogChromeHeader
        title="Rename panel"
        titleId="rename-multiplex-panel-title"
        onClose={close}
        sx={{ '& .MuiTypography-h1': { fontSize: 20, lineHeight: 1.35 } }}
      />
      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Panel name"
          value={name}
          disabled={rename.isLoading}
          inputProps={{ maxLength: 100 }}
          error={!trimmedName}
          helperText={!trimmedName ? 'Enter a panel name.' : 'Shown wherever this panel is listed.'}
          onChange={(event) => {
            setName(event.target.value);
            rename.reset();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && dirty && trimmedName) rename.mutate();
          }}
        />
        {rename.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage(rename.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogChromeFooter>
        {dirty ? (
          <>
            <Button
              variant="contained"
              color="warning"
              disabled={rename.isLoading}
              onClick={() => {
                setName(currentName);
                rename.reset();
              }}
            >
              Discard changes
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!trimmedName || rename.isLoading}
              onClick={() => rename.mutate()}
            >
              Save
            </Button>
          </>
        ) : (
          <Button variant="outlined" color="inherit" onClick={close}>
            Close
          </Button>
        )}
      </DialogChromeFooter>
    </Dialog>
  );
};
